import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useChangePasswordMutation } from '../../../api/use-change-password-mutation';
import PasswordField from './PasswordField';
import { companyInfoPrimaryButtonSx } from './companyInfoStyles';
import {
  getPasswordStrengthChecks,
  getPasswordUsername,
  PASSWORD_REQUIREMENTS,
  passwordContainsUsername,
  validateChangePassword,
} from './securityValidation';

const Security = () => {
  const { partnerProfile, isPartner } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});

  const changePasswordMutation = useChangePasswordMutation({
    onSuccess: (result) => {
      toast.success(result.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
    },
    onError: (error) => {
      if (error.fieldErrors) {
        setErrors((prev) => ({ ...prev, ...error.fieldErrors }));
      }
      toast.error(error?.message || 'Failed to change password');
    },
  });

  const username = useMemo(() => getPasswordUsername(partnerProfile), [partnerProfile]);
  const isSubmitting = changePasswordMutation.isPending;

  const strength = getPasswordStrengthChecks(newPassword);
  const usernameMet = newPassword.length > 0 && !passwordContainsUsername(newPassword, username);
  const requirementMet = [
    strength.minLength,
    strength.uppercase,
    strength.lowercase,
    strength.number,
    strength.special,
    usernameMet,
  ];

  const clearFieldError = useCallback((field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!isPartner) {
      toast.error('Partner access is required to change your password.');
      return;
    }

    const validationErrors = validateChangePassword({
      currentPassword,
      newPassword,
      confirmPassword,
      username,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    changePasswordMutation.mutate({ currentPassword, newPassword });
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: 'text.primary', mb: 1.25 }}>
        Change Password
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 0.75 }}>
        In order to protect your account, make sure your password:
      </Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          mb: 2.75,
          pl: 2.5,
          fontSize: '0.8125rem',
          color: 'text.secondary',
          lineHeight: 1.7,
        }}
      >
        {PASSWORD_REQUIREMENTS.map((label, index) => {
          const met = requirementMet[index];
          return (
            <Box
              component="li"
              key={label}
              sx={{
                color: met ? 'success.main' : 'text.secondary',
                fontWeight: met ? 600 : 400,
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>

      <PasswordField
        id="security-current-password"
        label="Current Password"
        placeholder="Current Password"
        value={currentPassword}
        onChange={(event) => {
          setCurrentPassword(event.target.value);
          clearFieldError('currentPassword');
        }}
        error={errors.currentPassword}
        disabled={isSubmitting}
        autoComplete="current-password"
      />

      <PasswordField
        id="security-new-password"
        label="New Password"
        placeholder="New Password"
        value={newPassword}
        onChange={(event) => {
          setNewPassword(event.target.value);
          clearFieldError('newPassword');
        }}
        error={errors.newPassword}
        disabled={isSubmitting}
        autoComplete="new-password"
      />

      <PasswordField
        id="security-confirm-password"
        label="Confirm New Password"
        placeholder="Confirm New Password"
        value={confirmPassword}
        onChange={(event) => {
          setConfirmPassword(event.target.value);
          clearFieldError('confirmPassword');
        }}
        error={errors.confirmPassword}
        disabled={isSubmitting}
        autoComplete="new-password"
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={isSubmitting}
          sx={companyInfoPrimaryButtonSx}
        >
          {isSubmitting ? 'Updating…' : 'Update Password'}
        </Button>
      </Box>
    </Box>
  );
};

export default Security;
